/**
 * Integration with Podscribe (https://podscribe.com), the podcast attribution vendor used by the
 * Right Side Up media agency. index.html loads the tag once OneTrust targeting consent (C0004) is
 * granted and fires the visit event; this module reports purchases.
 *
 * @see https://app.podscribe.com/pixel-code/fi
 */
import analytics, { getTrackedProductsValue, shouldTrackMarketingEvent } from '.';
import { isTrackingSuppressed } from '../../hooks/useTenancy';
import { hasTargetingConsent } from '../consent';
import store from '../reduxStore';
import { typedWindow } from '../Window';
import { ECOM_EVENT_ORDER_COMPLETED, SegmentPurchaseEventProperties } from './events';

type PodscribePurchase = {
  value: number;
  order_number: string;
  currency: string;
  discount_code?: string;
  hashed_email?: string;
};

/** Non-test production only, matching the other marketing pixels. */
function podscribeEnabled(): boolean {
  return !isTrackingSuppressed() && process.env.REACT_APP_ENVIRONMENT === 'production';
}

/**
 * sha256 hex of the trimmed, lowercased email, the form podscribe matches against. Resolves
 * undefined when hashing is unavailable so a missing hash never drops the purchase itself.
 */
export async function podscribeHashedEmail(email: string | undefined): Promise<string | undefined> {
  if (!email) {
    return undefined;
  }
  try {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(email.trim().toLowerCase()));
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
  } catch {
    return undefined;
  }
}

/**
 * Builds the purchase payload. Value is the tracked collar + subscription figure the other
 * marketing pixels report, so podscribe roas stays comparable to agentio, vibe, and northbeam.
 */
export function podscribePurchasePayload(
  properties: SegmentPurchaseEventProperties,
  hashedEmail?: string,
): PodscribePurchase {
  const purchase: PodscribePurchase = {
    value: getTrackedProductsValue(properties),
    order_number: properties.orderId,
    currency: properties.currency,
  };

  // podscribe raises a data issue for a purchase carrying an empty discount code, so the key is
  // only sent when the order actually used one.
  if (properties.code) {
    purchase.discount_code = properties.code;
  }

  // optional match key; omitted rather than sent empty, same as discount_code.
  if (hashedEmail) {
    purchase.hashed_email = hashedEmail;
  }

  return purchase;
}

function podscribeOrderCompleted(_messageId: string, properties: SegmentPurchaseEventProperties) {
  if (!podscribeEnabled()) {
    return;
  }

  const email = store.getState().session?.email ?? analytics.userTraits().email;

  // consent is read after the hash resolves, like the other marketing pixels, so a withdrawal
  // during hashing still drops the event. fiPodscribe queues until the tag has loaded and
  // initialized, so the confirmation page cannot lose this event to the script being in flight.
  void podscribeHashedEmail(email).then((hashedEmail) => {
    if (!hasTargetingConsent()) {
      return;
    }
    typedWindow.fiPodscribe?.('purchase', podscribePurchasePayload(properties, hashedEmail));
  });
}

// Map Segment events to Podscribe handlers
type PodscribeEventHandler = (messageId: string, properties: any) => void;
const PODSCRIBE_EVENT_MAP: Record<string, PodscribeEventHandler> = {
  [ECOM_EVENT_ORDER_COMPLETED]: podscribeOrderCompleted,
};

function onTrack(event: string, properties: any, messageId: string) {
  const handler = PODSCRIBE_EVENT_MAP[event];
  if (!handler || !shouldTrackMarketingEvent(properties)) {
    return;
  }
  handler(messageId, properties);
}

/** Wire Order Completed to the podscribe purchase event; safe to call pre-consent. */
export function podscribeSetupCallbacks() {
  analytics.onTrack(onTrack);
}
